import { FC, useContext } from "react";

import { FormCategory, FormType, getCurrencyFromCountry } from "@simplyk/common";

import { FrontendFormContext } from "../../../../contexts/FrontendFormContext";
import { ThemedPeerCredibilityChipGuestsCount } from "../../sharedComponents/ThemedPeerCredibilityChipGuestsCount";

import { useFormV2Context } from "@/features/FormV2/context/FormV2Context";
import { ThemedFormArchivedChip } from "@/features/FormV2/sharedComponents/ThemedFormArchivedChip";
import { ThemedFormBanner } from "@/features/FormV2/sharedComponents/ThemedFormBanner";
import { ThemedFormCampaignTargetThermometer } from "@/features/FormV2/sharedComponents/ThemedFormCampaignTargetThermometer";
import { ThemedFormDescription } from "@/features/FormV2/sharedComponents/ThemedFormDescription";
import { ThemedFormTitle } from "@/features/FormV2/sharedComponents/ThemedFormTitle";
import { FormV2LogoChip } from "@/features/FormV2/Step1/FormV2LogoChip";
import { ThemedDonationFormSummaryLayout } from "@/features/FormV2/Step1/Summary/ThemedDonationFormSummaryLayout";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import { FormCampaignTargetThermometerSkeleton } from "@/forms-design-system/FormCampaignTargetThermometer";
import { trpc } from "@/helpers/trpc";

type DonationFormV2SummaryProps = {
  forceInlined?: boolean;
};

export const DonationFormV2Summary: FC<DonationFormV2SummaryProps> = ({ forceInlined }) => {
  const { formObject, description, localeField, formOrganization } = useFormV2Context(FormType.DonationForm);
  const { livePeerCredibilityChipVariant, peerCredibilityTotalCount, peerCredibilityWeeklyCount } =
    useContext(FrontendFormContext);

  const { isPreview, currentStepKey: previewCurrentStepKey, placeholders } = usePreviewContext();
  const isThermometerGhostPreview = useIsGhostPreview("FormV2Thermometer");
  const isDescriptionGhostPreview = useIsGhostPreview("FormV2Description");

  const isCampaign = !!formObject.target;

  const { data: campaignVolumeData, isLoading: isCampaignLoading } = trpc.getStoredFormVolume.useQuery(
    {
      formId: formObject.id,
      formType: FormType.DonationForm,
    },
    {
      enabled: isCampaign && !isPreview,
    }
  );

  const currency = getCurrencyFromCountry(formOrganization.country);

  const title = localeField!.title;
  const banner = formObject.bannerUrl
    ? { imageSrc: formObject.bannerUrl }
    : formObject.bannerVideoUrl
      ? { videoSrc: formObject.bannerVideoUrl }
      : undefined;

  const campaign = isCampaign
    ? {
        isLoading: isCampaignLoading && !isPreview,
        currentCollectedAmount: isPreview ? (formObject?.target ?? 0) * 0.3 : (campaignVolumeData ?? 0),
        targetedAmount: formObject.target ?? 0,
        currency,
      }
    : undefined;

  return (
    <ThemedDonationFormSummaryLayout
      forceInlined={forceInlined}
      slots={{
        banner: banner && <ThemedFormBanner {...banner} />,
        organizationLogo: (
          <FormV2LogoChip organizationName={formObject.organization?.name} logoUrl={formObject.logoUrl} />
        ),
        status: formObject.isArchived && <ThemedFormArchivedChip />,
        title: <ThemedFormTitle>{title}</ThemedFormTitle>,
        peerCredibilityChipGuestsCount: livePeerCredibilityChipVariant && (
          <ThemedPeerCredibilityChipGuestsCount
            totalCount={peerCredibilityTotalCount}
            weeklyCount={peerCredibilityWeeklyCount}
            formCategory={FormCategory.DonationForm}
          />
        ),
        campaignTarget: campaign ? (
          campaign.isLoading ? (
            <FormCampaignTargetThermometerSkeleton />
          ) : (
            <ThemedFormCampaignTargetThermometer
              currentCollectedAmount={campaign.currentCollectedAmount}
              targetedAmount={campaign.targetedAmount}
              currency={campaign.currency}
              sx={{
                opacity: isThermometerGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
              }}
            />
          )
        ) : undefined,
        description: description && (
          <ThemedFormDescription
            description={description}
            isInitiallyExpanded={isPreview && previewCurrentStepKey === "description" && !placeholders.isDescription}
            sx={{
              opacity: isDescriptionGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
            }}
          />
        ),
      }}
    />
  );
};
